import React from 'react';
import { Link } from 'react-router-dom';
import {
  trackCalculatorFinancialCheckupCtaClick,
  trackCalculatorSendResultCtaClick,
  trackLinkClick,
} from '../lib/analytics';
import { BOOKING_PATH, GOAL_SETTING_MEETING, INTAKE_PATH } from '../config/planningOffers';

/** Price, length and refund line under the booking button; all terms come from the catalog. */
const MEETING_OFFER_LINE = `${GOAL_SETTING_MEETING.priceLabel} for ${GOAL_SETTING_MEETING.durationMinutes} minutes. ${GOAL_SETTING_MEETING.refundWording}`;

const DEFAULT_BODY = [
  'A calculator can show a direction, but the next decision usually depends on taxes, cash flow, timing, and tradeoffs that are specific to your household.',
  'Use your result as one input to a coordinated financial plan built around your complete household.',
];

function withCalculatorContext(to, calculatorId, calculatorName, ctaIntent) {
  const params = new URLSearchParams({
    cta: ctaIntent,
  });
  if (calculatorId) params.set('calculator_id', calculatorId);
  if (calculatorName) params.set('calculator_name', calculatorName);
  const separator = to.includes('?') ? '&' : '?';
  return `${to}${separator}${params.toString()}`;
}

export default function CalculatorDiscoveryCta({
  eyebrow = 'NEXT STEP',
  headline = 'Want help interpreting your result?',
  body = DEFAULT_BODY,
  preButtonLine,
  postButtonLine,
  trustRow,
  buttonText = GOAL_SETTING_MEETING.cta,
  offerLine = MEETING_OFFER_LINE,
  secondaryPrompt = 'Not ready for a call?',
  secondaryButtonText = "Send me your result and I'll point you in the right direction.",
  to = BOOKING_PATH,
  secondaryTo = INTAKE_PATH,
  buttonId = 'calculator_financial_plan_cta',
  secondaryButtonId = 'calculator_send_result_cta',
  className = '',
  calculatorId,
  calculatorName,
}) {
  const primaryTo = withCalculatorContext(to, calculatorId, calculatorName, 'book_meeting');
  const sendResultTo = withCalculatorContext(secondaryTo, calculatorId, calculatorName, 'send_result');
  const basePayload = {
    calculator_id: calculatorId,
    calculator_name: calculatorName,
    destination_type: 'contact_form',
  };

  const handlePrimaryClick = () => {
    const payload = {
      ...basePayload,
      destination_type: to === BOOKING_PATH ? 'meeting_booking' : 'contact_form',
      offer_id: to === BOOKING_PATH ? GOAL_SETTING_MEETING.offerId : undefined,
      button_id: buttonId,
      button_label: buttonText,
    };
    trackLinkClick(primaryTo, buttonText, payload);
    trackCalculatorFinancialCheckupCtaClick(payload);
  };

  const handleSecondaryClick = () => {
    const payload = {
      ...basePayload,
      button_id: secondaryButtonId,
      button_label: secondaryButtonText,
    };
    trackLinkClick(sendResultTo, secondaryButtonText, payload);
    trackCalculatorSendResultCtaClick(payload);
  };

  return (
    <div className={`pete-card pete-calculator-cta ${className}`.trim()}>
      {eyebrow ? <p className="pete-calculator-cta-eyebrow">{eyebrow}</p> : null}
      <h2>{headline}</h2>
      <div className="pete-calculator-cta-copy">
        {Array.isArray(body)
          ? body.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))
          : body}
      </div>
      {preButtonLine ? <p className="pete-calculator-cta-prebutton">{preButtonLine}</p> : null}
      <div className="pete-calculator-cta-actions">
        <Link
          className="pete-btn-primary pete-calculator-cta-button"
          to={primaryTo}
          data-button-id={buttonId}
          onClick={handlePrimaryClick}
        >
          {buttonText}
        </Link>
        {offerLine && to === BOOKING_PATH ? <p className="pete-calculator-cta-offer">{offerLine}</p> : null}
        <div className="pete-calculator-cta-secondary">
          <p>{secondaryPrompt}</p>
          <Link
            className="pete-calculator-cta-secondary-link"
            to={sendResultTo}
            data-button-id={secondaryButtonId}
            onClick={handleSecondaryClick}
          >
            {secondaryButtonText}
          </Link>
        </div>
      </div>
      {postButtonLine ? <p className="pete-calculator-cta-postbutton">{postButtonLine}</p> : null}
      {trustRow || null}
    </div>
  );
}
